import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
  ReactNode,
} from "react"
import Commander from "../commander"
import type {
  Command,
  CommandKey,
  CommandCategory,
  SearchResult,
  SearchOptions,
  ExecutionResult,
} from "../types"

// ============================================================================
// CONTEXT TYPES
// ============================================================================

interface CommanderContextValue {
  // Core commander instance
  commander: Commander

  // Command management
  commands: () => Command[]
  add: (command: Command) => Command
  remove: (commandOrKey: string | CommandKey | Command) => boolean
  removeByOwner: (owner: string) => number
  removeByCategory: (category: CommandCategory) => number
  has: (key: CommandKey) => boolean
  getCommand: (key: CommandKey) => Command | undefined

  // Execution
  invoke: <T = any>(
    key: CommandKey,
    input?: any,
    source?: "palette" | "shortcut" | "api",
  ) => Promise<T>
  attempt: <T = any>(
    key: CommandKey,
    input?: any,
    source?: "palette" | "shortcut" | "api",
  ) => Promise<ExecutionResult<T>>

  // Search & filtering
  search: (query: string, options?: SearchOptions) => Promise<SearchResult[]>
  isCommandAvailable: (command: Command) => Promise<boolean>
  getAvailableCommands: (options?: {
    category?: CommandCategory
    owner?: string
  }) => Promise<Command[]>

  // Organization
  getCommandsByCategory: () => Map<CommandCategory, Command[]>
  getCategories: () => CommandCategory[]

  // History & tracking
  getExecutionHistory: (limit?: number) => any[]
  getRecentCommands: () => Command[]
  clearHistory: () => void

  // Events
  listen: (
    event: string,
    callback: (...args: any[]) => void | Promise<void>,
    options?: { once?: boolean },
  ) => any
  removeListener: (listener: any) => void

  // Utilities
  createInvoker: (key: CommandKey) => any
  getStats: () => any

  // State
  isReady: boolean
}

const CommanderContext = createContext<CommanderContextValue | null>(null)

// ============================================================================
// PROVIDER PROPS
// ============================================================================

interface CommanderProviderProps {
  children: ReactNode
  commander: Commander
  onReady?: (commander: Commander) => void
  enableDevTools?: boolean
}

// ============================================================================
// PROVIDER COMPONENT
// ============================================================================

export function CommanderProvider({
  children,
  commander,
  onReady,
  enableDevTools = process.env.NODE_ENV === "development",
}: CommanderProviderProps) {
  // Setup dev tools and callbacks
  useEffect(() => {
    // Dev tools integration
    if (enableDevTools && typeof window !== "undefined") {
      // Expose commander globally for debugging
      ;(window as any).__commander = commander

      // Add dev commands
      commander.add({
        key: "dev:inspect-commands" as CommandKey,
        label: "Inspect All Commands",
        description: "Log all registered commands to console",
        category: "debug",
        icon: "🔍",
        owner: "dev-tools",
        tags: ["debug", "inspect"],
        priority: 100,
        handle: async () => {
          console.group("📋 Registered Commands")
          commander.commands().forEach((cmd) => {
            console.log(`${cmd.icon || "📝"} ${cmd.label}`, {
              key: cmd.key,
              category: cmd.category,
              owner: cmd.owner,
              shortcut: cmd.shortcut,
              tags: cmd.tags,
            })
          })
          console.groupEnd()
          return {
            commands: commander.commands(),
            total: commander.commands().length,
          }
        },
      })

      commander.add({
        key: "dev:clear-history" as CommandKey,
        label: "Clear Command History",
        description: "Clear execution history and recent commands",
        category: "debug",
        icon: "🧹",
        owner: "dev-tools",
        tags: ["debug", "clear"],
        priority: 90,
        handle: async () => {
          commander.clearHistory()
          console.log("Command history cleared")
          return { cleared: true }
        },
      })

      commander.add({
        key: "dev:stats" as CommandKey,
        label: "Show Commander Stats",
        description: "Display commander statistics",
        category: "debug",
        icon: "📊",
        owner: "dev-tools",
        tags: ["debug", "stats"],
        priority: 80,
        handle: async () => {
          const stats = commander.getStats()
          console.log("Commander Stats:", stats)
          return stats
        },
      })

      commander.add({
        key: "dev:search-test" as CommandKey,
        label: "Test Search System",
        description: "Test search functionality with sample queries",
        category: "debug",
        icon: "🔎",
        owner: "dev-tools",
        tags: ["debug", "search"],
        priority: 70,
        handle: async () => {
          const queries = ["save", "debug", "file", "system"]
          const results: any = {}

          for (const query of queries) {
            const searchResults = await commander.search(query, { limit: 3 })
            results[query] = searchResults.map((r) => ({
              label: r.command.label,
              score: r.score,
              matched: r.matchedTerms,
            }))
          }

          console.log("Search Test Results:", results)
          return results
        },
      })
    }

    // Call onReady callback
    onReady?.(commander)

    // Cleanup dev tools on unmount
    return () => {
      if (enableDevTools && typeof window !== "undefined") {
        delete (window as any).__commander
        commander.removeByOwner("dev-tools")
      }
    }
  }, [commander, enableDevTools, onReady])

  // Context value with all commander methods
  const value = useMemo<CommanderContextValue>(
    () => ({
      // Core instance
      commander,

      // Command management
      commands: () => commander.commands(),
      add: (command: Command) => commander.add(command),
      remove: (commandOrKey: string | CommandKey | Command) =>
        commander.remove(commandOrKey),
      removeByOwner: (owner: string) => commander.removeByOwner(owner),
      removeByCategory: (category: CommandCategory) =>
        commander.removeByCategory(category),
      has: (key: CommandKey) => commander.has(key),
      getCommand: (key: CommandKey) => commander.getCommand(key),

      // Execution
      invoke: <T = any,>(
        key: CommandKey,
        input?: any,
        source: "palette" | "shortcut" | "api" = "api",
      ) => commander.invoke<T>(key, input, source),
      attempt: <T = any,>(
        key: CommandKey,
        input?: any,
        source: "palette" | "shortcut" | "api" = "api",
      ) => commander.attempt<T>(key, input, source),

      // Search & filtering
      search: (query: string, options?: SearchOptions) =>
        commander.search(query, options),
      isCommandAvailable: (command: Command) =>
        commander.isCommandAvailable(command),
      getAvailableCommands: (options?: {
        category?: CommandCategory
        owner?: string
      }) => commander.getAvailableCommands(options),

      // Organization
      getCommandsByCategory: () => commander.getCommandsByCategory(),
      getCategories: () => commander.getCategories(),

      // History & tracking
      getExecutionHistory: (limit?: number) =>
        commander.getExecutionHistory(limit),
      getRecentCommands: () => commander.getRecentCommands(),
      clearHistory: () => commander.clearHistory(),

      // Events
      listen: (
        event: string,
        callback: (...args: any[]) => void | Promise<void>,
        options?: { once?: boolean },
      ) => commander.listen(event, callback, options),
      removeListener: (listener: any) => commander.removeListener(listener),

      // Utilities
      createInvoker: (key: CommandKey) => commander.createInvoker(key),
      getStats: () => commander.getStats(),

      // State
      isReady: true,
    }),
    [commander],
  )

  return (
    <CommanderContext.Provider value={value}>
      {children}
    </CommanderContext.Provider>
  )
}

// ============================================================================
// HOOK TO ACCESS CONTEXT
// ============================================================================

/**
 * Hook to access the Commander context
 * Returns the full Commander API
 */
export function useCommander(): CommanderContextValue {
  const context = useContext(CommanderContext)

  if (!context) {
    throw new Error(
      "useCommander must be used within a CommanderProvider. " +
        "Wrap your app with <CommanderProvider> to use commander hooks.",
    )
  }

  if (!context.isReady) {
    throw new Error("Commander is not ready yet.")
  }

  return context
}

// ============================================================================
// CONVENIENCE HOOKS
// ============================================================================

/**
 * Hook to get just the commander instance
 */
export function useCommanderInstance(): Commander {
  const { commander } = useCommander()
  return commander
}

/**
 * Hook for direct command execution
 */
export function useInvoke() {
  const { invoke } = useCommander()
  return invoke
}

/**
 * Hook for safe command execution
 */
export function useAttempt() {
  const { attempt } = useCommander()
  return attempt
}

/**
 * Hook for command search
 */
export function useSearch() {
  const { search } = useCommander()
  return search
}

/**
 * Hook to get all commands
 */
export function useCommands() {
  const { commands } = useCommander()
  return commands()
}

/**
 * Hook to get commands by category
 */
export function useCommandsByCategory() {
  const { getCommandsByCategory } = useCommander()
  return getCommandsByCategory()
}

/**
 * Hook to get recent commands
 */
export function useRecentCommands() {
  const { getRecentCommands } = useCommander()
  return getRecentCommands()
}

/**
 * Hook to get command stats
 */
export function useCommanderStats() {
  const { getStats } = useCommander()
  return getStats()
}
